import { useEffect, useState } from "react";
import type { RendererProps } from "../core/element-registry";
import { useRenderContext } from "../core/render-context";
import { useAtomDefaults } from "../contexts/atom-defaults-context";
import { useBoundValue } from "../hooks/use-bound-value";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../components/ui/select";
import { stripPresetConflicts } from "../lib/preset-utils";
import { ElementRenderer, liftV4Props } from "./element-renderer";

/** Extract last segment from a bind path. E.g., "#{form.category}" -> "category" */
function extractFieldName(bind?: string): string | undefined {
  if (!bind) return undefined;
  const stripped = bind.replace(/^#\{/, "").replace(/\}$/, "");
  const segments = stripped.split(".");
  return segments[segments.length - 1];
}

function humanizeFieldName(fieldName?: string): string | undefined {
  if (!fieldName) return undefined;
  const readable = fieldName
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim();
  if (!readable) return undefined;
  return readable.charAt(0).toUpperCase() + readable.slice(1);
}

function resolveAccessibleText(
  value: string | undefined,
  resolveSymbol: (value: string) => unknown,
): string | undefined {
  if (!value) return undefined;
  const resolved = resolveSymbol(value);
  const candidate = String(resolved ?? value).trim();
  if (!candidate || /^(?:#|@)\{.*\}$/.test(candidate)) return undefined;
  return candidate;
}

function resolveBoolean(
  value: boolean | string | undefined,
  resolveSymbol: (value: string) => unknown,
): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value !== "string") return false;
  const resolved = /^(?:#|@)\{.*\}$/.test(value) ? resolveSymbol(value) : value;
  if (typeof resolved === "boolean") return resolved;
  return String(resolved).trim().toLowerCase() === "true";
}

function normalizeSingleValue(raw: unknown, fallback = ""): string {
  if (Array.isArray(raw)) {
    return raw.length > 0 ? String(raw[raw.length - 1] ?? fallback) : fallback;
  }
  return raw == null || raw === "" ? fallback : String(raw);
}

function normalizeMultipleValue(raw: unknown, fallback: string[] = []): string[] {
  let candidates: unknown[];
  if (Array.isArray(raw)) {
    candidates = raw;
  } else if (typeof raw === "string" && raw.trim().startsWith("[")) {
    try {
      const parsed = JSON.parse(raw) as unknown;
      candidates = Array.isArray(parsed) ? parsed : [raw];
    } catch {
      candidates = [raw];
    }
  } else if (raw == null || raw === "") {
    candidates = fallback;
  } else {
    candidates = [raw];
  }

  return [...new Set(candidates.map(String).filter(Boolean))];
}

function decodeMultipleValue(encoded: string): string[] {
  try {
    return normalizeMultipleValue(JSON.parse(encoded));
  } catch {
    return normalizeMultipleValue(encoded);
  }
}

function resolveTriggerClassName(
  rootClassName: string | undefined,
  triggerClassName: string | undefined,
): string | undefined {
  return triggerClassName?.trim() ? triggerClassName : rootClassName;
}

export function SelectRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent, itemContext } = useRenderContext();
  const el = element as unknown as Record<string, unknown>;
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("select");
  const resolvedVariant =
    (element as { variant?: string }).variant ??
    atomDefaults.defaultVariant ??
    "default";
  const resolvedSize =
    (element as { size?: string }).size ??
    atomDefaults.defaultSize ??
    "default";
  const placeholder = element.placeholder
    ? String(resolveSymbol(element.placeholder) ?? "")
    : undefined;
  const authoredOpenRaw = el.open ?? el.defaultOpen;
  const hasAuthoredOpen = authoredOpenRaw !== undefined;
  const authoredOpen = hasAuthoredOpen
    ? resolveBoolean(
        authoredOpenRaw as boolean | string | undefined,
        resolveSymbol,
      )
    : false;
  const [open, setOpen] = useState(authoredOpen);

  useEffect(() => {
    setOpen(authoredOpen);
  }, [element, authoredOpen]);

  const multiple = resolveBoolean(
    (element as unknown as { multiple?: boolean | string }).multiple,
    resolveSymbol,
  );
  const authoredDefault = (
    element as unknown as { defaultValue?: unknown }
  ).defaultValue;
  const resolvedDefault =
    typeof authoredDefault === "string"
      ? (resolveSymbol(authoredDefault) ?? authoredDefault)
      : authoredDefault;
  const defaultMultipleValue = normalizeMultipleValue(resolvedDefault);
  // Encode multi-select arrays before passing them through useBoundValue. The
  // hook compares external values with Object.is; a stable string prevents a
  // resolver that returns an equivalent fresh array from causing render loops.
  const fallbackValue = multiple
    ? JSON.stringify(defaultMultipleValue)
    : normalizeSingleValue(resolvedDefault);
  const [localBoundValue, setLocalBoundValue] = useBoundValue<string>(
    element.bind,
    resolveSymbol,
    fallbackValue,
    (raw) =>
      multiple
        ? JSON.stringify(normalizeMultipleValue(raw, defaultMultipleValue))
        : normalizeSingleValue(raw, normalizeSingleValue(resolvedDefault)),
  );
  let boundValue = multiple ? "" : localBoundValue;
  let boundValues = multiple
    ? decodeMultipleValue(localBoundValue)
    : boundValue
      ? [boundValue]
      : [];

  const handleSingleValueChange = (value: string | null | undefined) => {
    const nextValue = value ?? "";
    setLocalBoundValue(nextValue);
    dispatchEvent(element.id, "change", {
      ...(itemContext ?? {}),
      value: nextValue,
    });
  };
  const handleMultipleValueChange = (value: string[]) => {
    const nextValues = normalizeMultipleValue(value);
    setLocalBoundValue(JSON.stringify(nextValues));
    dispatchEvent(element.id, "change", {
      ...(itemContext ?? {}),
      value: nextValues,
      values: nextValues,
      selectedValues: nextValues,
    });
  };
  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    dispatchEvent(element.id, "openChange", {
      ...(itemContext ?? {}),
      open,
    });
  };

  // Support both formats: element.options [{label,value}] and element.children [{type:"option"}]
  const rawOptions = (element as unknown as Record<string, unknown>).options;
  // If options is a string expression (e.g. "#{ui.itemGroups}"), resolve it
  const resolvedOptions =
    typeof rawOptions === "string" ? resolveSymbol(rawOptions) : rawOptions;
  const optionsFromProp = Array.isArray(resolvedOptions)
    ? (resolvedOptions as Array<{
        label?: string;
        value?: string;
        disabled?: boolean | string;
      }>)
    : undefined;
  // JITRULE Task 2: option-shaped children (both the flat `option` shorthand
  // and the compound select-content children) may nest their fields under
  // `props` — the filter predicate itself reads `.text`, so lift before
  // filtering, not after.
  const childOptions =
    element.children?.map(liftV4Props).filter((c) => c.type === "option" || c.text) ?? [];
  const rawTriggerChild = element.children?.find(
    (child) => child.type === "select-trigger",
  );
  const triggerChild = rawTriggerChild ? liftV4Props(rawTriggerChild) : undefined;
  const rawContentChild = element.children?.find(
    (child) => child.type === "select-content",
  );
  const contentChild = rawContentChild ? liftV4Props(rawContentChild) : undefined;
  const authoredContentOptions = (contentChild?.children ?? []).map(liftV4Props).map((child) => {
    const rawValue = child.value ?? child.variant ?? child.id;
    const value = String(resolveSymbol(String(rawValue)) ?? rawValue);
    const rawLabel = child.text ?? child.label ?? rawValue;
    return {
      element: child,
      value,
      label: String(resolveSymbol(String(rawLabel)) ?? rawLabel),
      disabled: resolveBoolean(child.disabled, resolveSymbol),
    };
  });

  // Dynamic options from state: optionsData is a state path, valueKey/labelKey define mapping
  const dynamicData = el.optionsData
    ? resolveSymbol(el.optionsData as string)
    : undefined;
  const dynamicOptions = Array.isArray(dynamicData)
    ? (dynamicData as Record<string, unknown>[])
    : undefined;
  const valueKey = String(el.valueKey ?? "value");
  const labelKey = String(el.labelKey ?? "label");

  // Merge: static options first, then dynamic options
  // Capture empty-value option labels to use as placeholder fallback
  let emptyOptionLabel: string | undefined;
  const allOptions: Array<{ label: string; value: string; disabled: boolean }> =
    [];
  if (optionsFromProp) {
    for (const opt of optionsFromProp) {
      const v = String(opt.value ?? "");
      if (!v) {
        // Radix Select requires non-empty value, but capture the label as placeholder fallback
        emptyOptionLabel = String(resolveSymbol(opt.label ?? "") ?? "");
        continue;
      }
      allOptions.push({
        label: String(resolveSymbol(opt.label ?? "") ?? ""),
        value: v,
        disabled: resolveBoolean(opt.disabled, resolveSymbol),
      });
    }
  }
  if (dynamicOptions) {
    for (const item of dynamicOptions) {
      const v = String(item[valueKey] ?? "");
      const l = String(item[labelKey] ?? v);
      if (v) {
        allOptions.push({
          label: l,
          value: v,
          disabled: resolveBoolean(
            item.disabled as boolean | string | undefined,
            resolveSymbol,
          ),
        });
      }
    }
  }

  // Preserve the historical single-select fallback. Multi-select intentionally
  // keeps an empty array empty so it can represent "no selections" honestly.
  const availableOptions = [...authoredContentOptions, ...allOptions];
  const firstEnabledOption = availableOptions.find((opt) => !opt.disabled);
  if (!multiple && boundValue === "" && firstEnabledOption && !placeholder) {
    boundValue = firstEnabledOption.value;
    boundValues = [boundValue];
  }

  // Fallback placeholder: explicit placeholder > empty-value option label > first option label
  const effectivePlaceholder =
    placeholder ??
    emptyOptionLabel ??
    (availableOptions.length > 0 ? availableOptions[0].label : undefined);
  const selectedLabel = multiple
    ? boundValues
        .map(
          (value) =>
            availableOptions.find((opt) => opt.value === value)?.label ?? value,
        )
        .join(", ") || effectivePlaceholder || ""
    : availableOptions.find((opt) => opt.value === boundValue)?.label ??
      effectivePlaceholder ??
      "";

  const fieldName = extractFieldName(element.bind);
  const accessibleLabel =
    resolveAccessibleText(element.ariaLabel, resolveSymbol) ??
    resolveAccessibleText(element.label, resolveSymbol) ??
    humanizeFieldName(resolveAccessibleText(element.name, resolveSymbol)) ??
    humanizeFieldName(fieldName) ??
    placeholder?.trim() ??
    "Select";
  const accessibleDescription = resolveAccessibleText(
    element.title,
    resolveSymbol,
  );
  const disabled = resolveBoolean(element.disabled, resolveSymbol);
  const required = resolveBoolean(element.required, resolveSymbol);
  const errorMessage = resolveAccessibleText(element.error, resolveSymbol);
  const invalid =
    resolveBoolean(element.invalid, resolveSymbol) ||
    Boolean(errorMessage?.trim());
  const errorId = errorMessage ? `${element.id}-error` : undefined;

  // base-ui Select.Root: pass both property options and authored compound
  // content items so Value can resolve the label for either representation.
  const selectItems = availableOptions.map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));
  const triggerDisabled = resolveBoolean(triggerChild?.disabled, resolveSymbol);
  const effectiveDisabled = disabled || triggerDisabled;
  const triggerEvents = triggerChild?.events && typeof triggerChild.events === "object"
    ? triggerChild.events as Record<string, unknown>
    : {};
  const triggerClickBinding = triggerEvents.click ?? triggerEvents.tap;
  const handleTriggerClick = triggerChild && triggerClickBinding !== undefined
    ? () => {
        const params = triggerClickBinding && typeof triggerClickBinding === "object"
          ? (triggerClickBinding as Record<string, unknown>).params
          : undefined;
        dispatchEvent(
          triggerChild.id,
          triggerEvents.click !== undefined ? "click" : "tap",
          params && typeof params === "object"
            ? { ...(params as Record<string, unknown>), ...(itemContext ?? {}) }
            : { ...(itemContext ?? {}) },
        );
      }
    : undefined;

  const selectChildren = (
    <>
      <SelectTrigger
        id={element.id}
        className={stripPresetConflicts(
          resolveTriggerClassName(element.className, triggerChild?.className),
          undefined,
          element.designAuthorityClassName,
        )}
        aria-label={accessibleLabel}
        aria-invalid={invalid || undefined}
        aria-describedby={errorId}
        aria-errormessage={errorId}
        disabled={effectiveDisabled}
        title={accessibleDescription}
        data-action="select"
        data-field-name={fieldName}
        data-field-type="select"
        data-atom-type="select"
        data-variant={resolvedVariant}
        data-size={resolvedSize}
        data-cogo-element-id={triggerChild?.id ?? element.id}
        data-cogo-type={triggerChild?.type ?? element.type}
        data-multiple={multiple ? "true" : "false"}
        data-value={multiple ? JSON.stringify(boundValues) : boundValue}
        data-values={JSON.stringify(boundValues)}
        data-open={open ? "true" : "false"}
        onClick={handleTriggerClick}
      >
        <SelectValue placeholder={effectivePlaceholder}>
          {selectedLabel}
        </SelectValue>
        {triggerChild?.children?.map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))}
      </SelectTrigger>
      <SelectContent
        className={contentChild?.className}
        data-cogo-element-id={contentChild?.id}
        data-cogo-type={contentChild?.type}
      >
        {authoredContentOptions.map((opt, i) => (
          <SelectItem
            key={opt.element.id || i}
            value={opt.value}
            disabled={opt.disabled}
            className={opt.element.className}
            data-cogo-element-id={opt.element.id}
            data-cogo-type={opt.element.type}
          >
            {opt.label}
            {opt.element.children?.map((child) => (
              <ElementRenderer key={child.id} element={child} />
            ))}
          </SelectItem>
        ))}
        {allOptions.map((opt, i) => (
          <SelectItem
            key={opt.value || i}
            value={opt.value}
            disabled={opt.disabled}
          >
            {opt.label}
          </SelectItem>
        ))}
        {allOptions.length === 0 && authoredContentOptions.length === 0
          ? childOptions.map((opt, i) => {
              const text = String(resolveSymbol(opt.text ?? "") ?? "");
              return (
                <SelectItem
                  key={opt.id || i}
                  value={opt.variant || text}
                  disabled={resolveBoolean(opt.disabled, resolveSymbol)}
                >
                  {text}
                </SelectItem>
              );
            })
          : null}
      </SelectContent>
      {errorMessage ? (
        <p
          id={errorId}
          role="alert"
          data-slot="select-error"
          className="cn-select-error mt-1.5 text-sm text-destructive"
        >
          {errorMessage}
        </p>
      ) : null}
    </>
  );

  const commonRootProps = {
    items: selectItems,
    name: element.name,
    disabled: effectiveDisabled,
    required,
    open,
    onOpenChange: handleOpenChange,
  };

  if (multiple) {
    const valueProps =
      element.bind !== undefined
        ? { value: boundValues }
        : { defaultValue: boundValues };
    return (
      <Select
        {...commonRootProps}
        {...valueProps}
        multiple
        onValueChange={handleMultipleValueChange}
      >
        {selectChildren}
      </Select>
    );
  }

  const valueProps =
    element.bind !== undefined
      ? { value: boundValue }
      : { defaultValue: boundValue };
  return (
    <Select
      {...commonRootProps}
      {...valueProps}
      onValueChange={handleSingleValueChange}
    >
      {selectChildren}
    </Select>
  );
}
